/**
 * Admin Portal → Codes → Store inventory — `/admin/codes/store` (every App Store code batch, counts
 * only) and its Store setup tab, `/admin/codes/store/setup` (the store keys, the discount offers and
 * the partner code supply — the page that was Partners → Store Access). Codes PR-8; the lead's
 * unified-codes design §2.1.
 *
 * Both supplies in one table: the partner program's App Store codes (made by the server) and the
 * Summer50 reward codes, which keep their own supply — created by hand in App Store Connect and
 * imported here with "Import a Summer50 reward CSV" (super_admin; until now it had no button). App
 * Store codes are bearer codes: never listed, only counted.
 *
 * Every admin role reads the counts; the import is super_admin only (hidden until the role is known —
 * fail closed; the server answers 403 anyway).
 *
 * Web-only (the admin SPA).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AdminButton, AdminCard } from '../../../components/admin/ui';
import { NoticeLine, fmtDate, hint, input, label, row, td, th, type Notice } from '../partners/PartnersShared';
import { AdminStoreAccess } from './AdminStoreAccess';
import { CodesAdmin } from './codesAdminService';
import {
  CODES_ROUTES, REWARD_SKUS, familyLabel, rewardImportError, storeBatchCounts, storeBatchDrifts, storeBatchOffer,
  type InventorySummary, type RewardImportForm,
} from './codesAreaLogic';
import { CodesHeader, ErrorLine, PAGE, SubTabs, readModelError, useCodesRole } from './CodesArea';

const TABS = [
  { to: CODES_ROUTES.store, label: 'Inventory', exact: true },
  { to: CODES_ROUTES.storeSetup, label: 'Store setup' },
];

export function CodesStoreInventory({ view = 'inventory' }: { view?: 'inventory' | 'setup' }) {
  return (
    <>
      <div style={{ ...PAGE, paddingBottom: 0 }}>
        <CodesHeader title="Store inventory" subtitle="The App Store code supply — the partner program's and the Summer50 rewards' — and the store setup behind it." />
        <SubTabs tabs={TABS} />
      </div>
      {view === 'setup' ? <AdminStoreAccess /> : <InventoryView />}
    </>
  );
}

const InventoryView: React.FC = () => {
  const { superAdmin, loaded } = useCodesRole();
  const [inv, setInv] = useState<InventorySummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    CodesAdmin.inventory().then(setInv).catch((e) => setError(readModelError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const batches = inv?.batches ?? [];
  return (
    <div style={{ ...PAGE, paddingTop: 0 }}>
      <ErrorLine text={error} />
      {inv && (
        <>
          <AdminCard style={{ marginBottom: 16 }} data-testid="quarter">
            <div style={{ fontSize: 13 }}>
              <div><b>This quarter</b> (from {fmtDate(inv.quarter.start)}), App Store Connect allows {inv.quarter.limit.toLocaleString()} codes.</div>
              <div>{inv.quarter.cron.label}: {inv.quarter.cron.used.toLocaleString()} used, {inv.quarter.cron.remaining.toLocaleString()} left.</div>
              <div>{inv.quarter.estimate.label}: {inv.quarter.estimate.used.toLocaleString()} used, {inv.quarter.estimate.remaining.toLocaleString()} left.</div>
            </div>
          </AdminCard>
          <AdminCard style={{ marginBottom: 16 }}>
            <p style={{ ...hint, marginTop: 0 }}>
              Counts only — App Store codes are never shown. &quot;Available&quot; codes are unused; &quot;issued&quot; ones are handed out
              (Apple never tells Coach Cedar which were redeemed, so a Summer50 reward code&apos;s redemption is not known).
              Summer50 reward codes keep their own supply, imported by hand below.
            </p>
            {batches.length === 0 ? <div style={hint}>No App Store code batches yet.</div> : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="store-batches">
                  <thead>
                    <tr>
                      <th style={th}>Supply</th><th style={th}>Codes for</th><th style={th}>Made by</th><th style={th}>Counts</th>
                      <th style={th}>Available</th><th style={th}>Issued</th><th style={th}>Redeemed</th><th style={th}>Expired</th>
                      <th style={th}>Retired</th><th style={th}>Next expiry</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batches.map((b) => (
                      <tr key={b.batch_key}>
                        <td style={td}>{familyLabel(b.family)}{b.batch_ref ? <div style={hint}>{b.batch_ref}</div> : null}</td>
                        <td style={td}>{storeBatchOffer(b)}</td>
                        <td style={td}>{b.source.replace(/_/g, ' ')}</td>
                        <td style={{ ...td, color: storeBatchDrifts(b) ? 'var(--status-warning-text, #92400e)' : undefined }}>{storeBatchCounts(b)}</td>
                        <td style={td}>{b.available}</td>
                        <td style={td}>{b.issued}</td>
                        <td style={td}>{b.redeemed == null ? 'not known' : b.redeemed}</td>
                        <td style={td}>{b.expired}</td>
                        <td style={td}>{b.retired}</td>
                        <td style={td}>{fmtDate(b.next_free_expiry ?? b.expires_at ?? null)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </AdminCard>
        </>
      )}
      {loaded && superAdmin && <RewardImport onImported={load} />}
      {loaded && !superAdmin && <p style={hint}>Importing Summer50 reward codes and the store setup need a super admin.</p>}
    </div>
  );
};

/** "Import a Summer50 reward CSV": the file App Store Connect gives for a one-time-code batch. super_admin only. */
const RewardImport: React.FC<{ onImported: () => void }> = ({ onImported }) => {
  const [form, setForm] = useState<RewardImportForm>({ sku: REWARD_SKUS[0].sku, batchRef: '', expires: '', fileName: null });
  const fileRef = useRef<Blob | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, setBusy] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const problem = rewardImportError(form, today);

  const send = async () => {
    if (problem || !fileRef.current) { setNotice({ kind: 'error', text: problem ?? 'Choose the CSV.' }); return; }
    setBusy(true);
    setNotice(null);
    try {
      const r = await CodesAdmin.importRewardCsv(form, fileRef.current);
      setNotice({ kind: 'ok', text: `Imported ${r.imported} code${r.imported === 1 ? '' : 's'} for ${r.reward_sku}. Codes already imported were skipped.` });
      onImported();
    } catch (e) {
      setNotice({ kind: 'error', text: readModelError(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminCard data-testid="reward-import">
      <h2 style={{ fontSize: 16, margin: '0 0 4px' }}>Import a Summer50 reward CSV</h2>
      <p style={{ ...hint, marginTop: 0 }}>
        In App Store Connect, make a one-time-use code batch for the reward offer and download its CSV. Choose the file here:
        one code per line, or App Store Connect&apos;s own file. Codes already imported are skipped.
      </p>
      <NoticeLine notice={notice} onClose={() => setNotice(null)} />
      <div style={row}>
        <div>
          <label style={label} htmlFor="ri-sku">Reward</label>
          <select id="ri-sku" style={input} value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })}>
            {REWARD_SKUS.map((r) => <option key={r.sku} value={r.sku}>{r.label}</option>)}
          </select>
        </div>
        <div>
          <label style={label} htmlFor="ri-ref">Batch name</label>
          <input id="ri-ref" style={input} value={form.batchRef} maxLength={120}
            onChange={(e) => setForm({ ...form, batchRef: e.target.value })} placeholder="ASC batch reference" />
        </div>
        <div>
          <label style={label} htmlFor="ri-exp">Codes expire on</label>
          <input id="ri-exp" type="date" style={input} value={form.expires} onChange={(e) => setForm({ ...form, expires: e.target.value })} />
        </div>
        <div>
          <label style={label} htmlFor="ri-file">CSV file</label>
          <input id="ri-file" type="file" accept=".csv,text/csv,text/plain"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              fileRef.current = f;
              setForm({ ...form, fileName: f ? f.name : null });
            }} />
        </div>
        <AdminButton size="sm" disabled={busy || !!problem} onClick={() => void send()}>Import</AdminButton>
      </div>
      {problem && <p style={hint}>{problem}</p>}
    </AdminCard>
  );
};

export default CodesStoreInventory;
